import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Loader2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pf = async (endpoint, options = {}) => {
  const res = await fetch(`${API_URL}/api/platform${endpoint}`, {
    ...options, credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const statusOptions = [
  'new', 'contacted', 'qualified', 'proposal_in_progress',
  'proposal_sent', 'negotiation', 'won', 'lost'
];

const statusColor = (s) => {
  if (s === 'new') return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (s === 'contacted' || s === 'qualified') return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  if (s === 'proposal_in_progress' || s === 'proposal_sent') return 'bg-violet-500/10 text-violet-400 border-violet-500/20';
  if (s === 'negotiation') return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20';
  if (s === 'won') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (s === 'lost') return 'bg-red-500/10 text-red-400 border-red-500/20';
  return 'bg-slate-700/30 text-slate-400';
};

const PlatformPricingRequests = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const data = await pf(`/pricing-requests${params}`);
      setRequests(data.requests);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (requestId, newStatus) => {
    try {
      await pf(`/pricing-requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success('Status updated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-pricing-requests">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Pricing Requests</h1>
          <p className="text-xs text-slate-500">{total} enquiries</p>
        </div>
        <select
          className="h-8 px-2 text-xs bg-slate-900 border border-slate-800 rounded text-slate-300"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          {statusOptions.map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <Mail className="w-8 h-8 text-slate-600 mx-auto" />
            <p className="text-xs text-slate-500">No pricing requests yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-[10px] text-slate-500 font-medium">Company</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Contact</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Projects</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Needs</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Source</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Status</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map(r => (
                <TableRow key={r.request_id} className="border-slate-800/50 hover:bg-slate-800/30">
                  <TableCell>
                    <p className="text-xs font-medium text-slate-200">{r.company_name}</p>
                    <p className="text-[10px] text-slate-500">{r.city || ''}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-xs text-slate-300">{r.contact_name}</p>
                    <p className="text-[10px] text-slate-500">{r.work_email}</p>
                    <p className="text-[10px] text-slate-500">{r.phone}</p>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">{r.number_of_projects || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.channel_partner_access_required && <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700">CP</Badge>}
                      {r.api_access_required && <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700">API</Badge>}
                      {r.crm_used && <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700">{r.crm_used}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500">{r.source}</Badge>
                  </TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.request_id, v)}>
                      <SelectTrigger className="h-6 w-[120px] text-[10px] bg-transparent border-slate-700 text-slate-400">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {statusOptions.map(s => (
                          <SelectItem key={s} value={s} className="text-slate-300 text-xs">{s.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-500 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default PlatformPricingRequests;
